import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookOpen, FiShield, FiDollarSign, FiUsers, FiArrowRight, FiCheck } = FiIcons;

const Services = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: FiBookOpen,
      title: 'Skills Development',
      subtitle: 'Comprehensive Training Programs',
      description: 'Empowering agricultural workers with essential skills through structured learnership programmes and professional development initiatives.',
      features: [
        'Learnership Programme Management',
        'Training Facilitation',
        'Skills Assessment & Certification',
        'Workplace-based Learning',
        'Career Development Planning'
      ],
      color: 'green'
    },
    {
      icon: FiShield,
      title: 'Governance Advisory',
      subtitle: 'Compliance & Policy Implementation',
      description: 'Expert guidance on regulatory compliance, policy development, and governance structures to ensure your agricultural business operates efficiently.',
      features: [
        'Regulatory Compliance Audits',
        'Policy Development & Implementation',
        'Risk Management Strategies',
        'Corporate Governance',
        'Legal Framework Guidance'
      ],
      color: 'blue'
    },
    {
      icon: FiDollarSign,
      title: 'Business Training',
      subtitle: 'Finance for Non-Financial Managers',
      description: 'Specialized financial literacy training designed for agricultural managers and leaders who need to understand business finances.',
      features: [
        'Financial Management Basics',
        'Budget Planning & Control',
        'Cash Flow Management',
        'Investment Decision Making',
        'Financial Reporting & Analysis'
      ],
      color: 'yellow'
    },
    {
      icon: FiUsers,
      title: 'Mentoring & Coaching',
      subtitle: 'Farm Management Excellence',
      description: 'One-on-one mentoring and coaching services to develop leadership capabilities and operational excellence in farm management.',
      features: [
        'Leadership Development',
        'Operational Efficiency Coaching',
        'Strategic Planning Support',
        'Team Building & Management',
        'Performance Improvement Plans'
      ],
      color: 'purple'
    }
  ];

  const colorClasses = {
    green: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
      hover: 'hover:bg-green-50'
    },
    blue: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-50'
    },
    yellow: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-700',
      border: 'border-yellow-200',
      hover: 'hover:bg-yellow-50'
    },
    purple: {
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-50'
    }
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-green-700">Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive agricultural consulting services designed to transform your 
            business through people development and operational excellence.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className={`p-8 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                activeService === index 
                  ? `${colorClasses[service.color].border} ${colorClasses[service.color].bg}` 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => setActiveService(index)}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-start space-x-4">
                <div className={`p-4 rounded-full ${colorClasses[service.color].bg}`}>
                  <SafeIcon 
                    icon={service.icon} 
                    className={`w-8 h-8 ${colorClasses[service.color].text}`} 
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className={`text-sm font-medium mb-3 ${colorClasses[service.color].text}`}>
                    {service.subtitle}
                  </p>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Features List */}
                  <div className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <SafeIcon 
                          icon={FiCheck} 
                          className={`w-4 h-4 ${colorClasses[service.color].text}`} 
                        />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          className="text-center bg-gradient-to-r from-green-700 to-green-600 rounded-2xl p-12 text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl font-bold mb-4">
            Ready to Transform Your Agricultural Business?
          </h3>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Let our experienced consultants help you develop your workforce, 
            improve governance, and achieve operational excellence.
          </p>
          <motion.button
            className="bg-white text-green-700 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-colors duration-200 flex items-center space-x-2 mx-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <span>Schedule Consultation</span>
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;